import React, { useCallback } from 'react'
import {
  VocSoundbiteFrame,
  VocSoundbiteNoHeaderFrame
} from 'components/visualization/SoundbiteFrame'
import { PostMetric } from 'schemas/PostMetricSchema'
import StyledAvatar from 'components/visualization/SoundbiteFrame/StyledAvatar'
import HeaderInfo from 'components/visualization/SoundbiteFrame/HeaderInfo'
import LinkedDate from 'components/visualization/SoundbiteFrame/LinkedDate'
import { useMedia } from 'components/visualization/SoundbiteFrame/Media'
import { MediaClickHandler } from 'components/visualization/SoundbiteFrame/Media/MediaGrid'
import InternalContents from 'components/visualization/SoundbiteFrame/InternalContents/InternalContents'
import Divider from 'components/visualization/SoundbiteFrame/Divider'
import ViewOnIcon from 'components/visualization/SoundbiteFrame/ActionIcons/ViewOnIcon'
import LinkedVocIcon from 'components/visualization/SoundbiteFrame/DomainIcon/LinkedVocIcon'
import { useAppDispatch } from 'app/hooks'
import { openLightbox } from 'features/ui/uiSlice'

import EngagementsPanel from './EngagementsPanel'
import { useStreamData } from '../hooks'
import { useWidgetContext } from '../widgetContext'

interface VocPostProps {
  data: PostMetric
  /**
   * for test id
   */
  serialNumber: number
  onClick?: (content: any) => void
}
const VocPost = ({ data, serialNumber, onClick }: VocPostProps) => {
  const streamData = useStreamData(data)
  const { widgetSetting } = useWidgetContext()
  const dispatch = useAppDispatch()

  const mediaClickHandler: MediaClickHandler = useCallback(
    (mediaUrls, index) => {
      dispatch(openLightbox({ slides: mediaUrls, defaultIndex: index }))
    },
    [dispatch]
  )
  const { mediaController, mediaGrid } = useMedia(streamData, {
    defaultDisplayOption: widgetSetting.defaultMediaDisplayOption,
    onMediaClick: mediaClickHandler
  })

  const { isMediaDoc, sourceType, url, domain, title, authorName, authorHandle } = streamData
  const hasActionItems = isMediaDoc || !!(sourceType && url)
  const hasHeader = !!title || !!authorName || !!authorHandle

  if (!hasHeader) {
    return (
      <VocSoundbiteNoHeaderFrame
        avatar={<StyledAvatar streamData={streamData} />}
        dateItem={<LinkedDate streamData={streamData} />}
        domainIcon={<LinkedVocIcon streamData={streamData} />}
        internalContents={
          <>
            {hasHeader && <Divider />}
            <InternalContents streamData={streamData} />
            {hasActionItems && <Divider />}
          </>
        }
        actionItems={
          hasActionItems && (
            <>
              <Divider />
              {isMediaDoc && mediaController}
              {sourceType && url && <ViewOnIcon source={sourceType!} domain={domain!} url={url} />}
            </>
          )
        }
        mediaItems={isMediaDoc && mediaGrid}
        metadataItems={<EngagementsPanel data={data} />}
        serialNumber={serialNumber}
        onClick={() => onClick && onClick(streamData)}
      />
    )
  }

  return (
    <VocSoundbiteFrame
      serialNumber={serialNumber}
      avatar={<StyledAvatar streamData={streamData} />}
      headerInfo={<HeaderInfo streamData={streamData} />}
      dateItem={<LinkedDate streamData={streamData} />}
      domainIcon={<LinkedVocIcon streamData={streamData} />}
      internalContents={
        <>
          {hasHeader && <Divider />}
          <InternalContents streamData={streamData} />
          {hasActionItems && <Divider />}
        </>
      }
      actionItems={
        hasActionItems && (
          <>
            <Divider />
            {isMediaDoc && mediaController}
            {sourceType && url && <ViewOnIcon source={sourceType!} domain={domain!} url={url} />}
          </>
        )
      }
      mediaItems={isMediaDoc && mediaGrid}
      metadataItems={<EngagementsPanel data={data} />}
      onClick={() => onClick && onClick(streamData)}
    />
  )
}
export default VocPost
